import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import CheckboxChecked from 'src/assets/images/checkbox-checked.svg';
import CheckboxUnchecked from 'src/assets/images/checkbox-unchecked.svg';
import ChevronDown from 'src/assets/images/chevron-down.svg';
import { validateFormValues, VALIDATION_CONSTANTS, getFieldError, } from './types';
import MarkdownText from './MarkdownText';
import { getTurnstileSiteKey } from 'src/config';
/**
 * Gets localized string value for given locale
 * Falls back to en-US if locale not found
 */
const getLocalizedValue = (localizedStr, locale) => {
    if (!localizedStr)
        return '';
    return localizedStr[locale] || localizedStr['en-US'] || '';
};
/**
 * FieldLabel component - Renders field label with markdown support
 */
const FieldLabel = ({ field, locale, className, htmlFor, as = 'label' }) => {
    const labelText = getLocalizedValue(field.title, locale);
    const isRequired = field.validation?.required;
    if (as === 'div') {
        return (_jsxs("div", { className: className, children: [_jsx(MarkdownText, { children: labelText }), isRequired && ' *'] }));
    }
    return (_jsxs("label", { htmlFor: htmlFor, className: className, children: [_jsx(MarkdownText, { children: labelText }), isRequired && ' *'] }));
};
/**
 * Renders a text/email/number input field with enhanced validation
 */
const renderTextInput = (field, value, onChange, locale, classNames, isReadOnly, isDisabled, errorMessage) => {
    const inputType = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text';
    const hasError = !!errorMessage;
    const inputClassName = `${classNames?.input || ''} ${isReadOnly || isDisabled ? classNames?.inputDisabled || '' : ''} ${hasError ? 'error' : ''}`.trim();
    // Determine maxLength based on field type and validation rules
    let maxLength;
    if (field.type === 'email') {
        maxLength = VALIDATION_CONSTANTS.MAX_EMAIL_LENGTH;
    }
    else if (field.validation?.maxLength) {
        maxLength = field.validation.maxLength;
    }
    else if (field.type === 'text') {
        maxLength =
            field.fieldName === 'firstName' || field.fieldName === 'lastName'
                ? VALIDATION_CONSTANTS.MAX_NAME_LENGTH
                : VALIDATION_CONSTANTS.DEFAULT_TEXT_MAX_LENGTH;
    }
    // Determine minLength
    const minLength = field.validation?.minLength;
    return (_jsxs("div", { className: classNames?.field, children: [_jsx(FieldLabel, { field: field, locale: locale, className: classNames?.fieldLabel, htmlFor: field.fieldName }), _jsx("div", { className: classNames?.inputWrapper, children: _jsx("input", { id: field.fieldName, type: inputType, className: inputClassName, value: value, onChange: (e) => onChange(e.target.value), placeholder: getLocalizedValue(field.placeholder, locale), readOnly: isReadOnly, disabled: isDisabled, min: field.type === 'number' ? field.validation?.min?.toString() : undefined, max: field.type === 'number' ? field.validation?.max?.toString() : undefined, minLength: minLength, maxLength: maxLength, pattern: field.validation?.pattern, "aria-invalid": hasError, "aria-describedby": hasError ? `${field.fieldName}-error` : undefined }) }), errorMessage && (_jsx("p", { id: `${field.fieldName}-error`, className: classNames?.helperText, style: { color: '#d32f2f', marginTop: '4px', fontSize: '14px' }, children: errorMessage })), !errorMessage && field.helperText && (_jsx("p", { className: classNames?.helperText, children: getLocalizedValue(field.helperText, locale) }))] }));
};
/**
 * Renders a dropdown/select field
 * Note: Does not render children - those are handled in renderField
 */
const renderDropdown = (field, value, onChange, locale, classNames, isDisabled) => (_jsxs("div", { className: classNames?.field, children: [_jsx(FieldLabel, { field: field, locale: locale, className: classNames?.fieldLabel, htmlFor: field.fieldName }), _jsxs("div", { className: classNames?.dropdownWrapper, children: [_jsxs("select", { id: field.fieldName, className: classNames?.dropdown, value: value, onChange: (e) => onChange(e.target.value), disabled: isDisabled, children: [_jsx("option", { value: "", children: getLocalizedValue(field.placeholder, locale) || 'Select option' }), field.options?.map((option) => {
                            // Note: <option> elements don't support complex HTML/markdown,
                            // so we keep plain text here
                            return (_jsx("option", { value: option.value, children: getLocalizedValue(option.title, locale) }, option.value));
                        })] }), _jsx("img", { src: ChevronDown, alt: "", className: classNames?.dropdownIcon, "aria-hidden": "true" })] })] }, field.fieldName));
/**
 * Gets the children fields for a selected dropdown option
 */
const getDropdownChildren = (field, selectedValue) => {
    if (!field.options || !selectedValue)
        return [];
    const selectedOption = field.options.find(opt => opt.value === selectedValue);
    return selectedOption?.children || [];
};
/**
 * Custom multi-select dropdown component with checkboxes
 */
const MultiSelectDropdown = ({ field, selectedValues, onChange, locale, classNames, isDisabled }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);
    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    const handleToggle = (value) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newValues);
    };
    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return getLocalizedValue(field.placeholder, locale) || 'Select';
        }
        if (selectedValues.length === 1) {
            const option = field.options?.find(opt => opt.value === selectedValues[0]);
            return option ? getLocalizedValue(option.title, locale) : selectedValues[0];
        }
        return `${selectedValues.length} selected`;
    };
    return (_jsxs("div", { className: classNames?.field, ref: dropdownRef, children: [_jsx(FieldLabel, { field: field, locale: locale, className: classNames?.fieldLabel, htmlFor: field.fieldName }), _jsxs("div", { className: classNames?.dropdownWrapper, style: { position: 'relative' }, children: [_jsx("button", { id: field.fieldName, type: "button", className: classNames?.dropdown, onClick: () => !isDisabled && setIsOpen(!isOpen), disabled: isDisabled, style: {
                            width: '100%',
                            textAlign: 'left',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }, children: getDisplayText() }), _jsx("img", { src: ChevronDown, alt: "", className: classNames?.dropdownIcon, "aria-hidden": "true", style: {
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease',
                        } }), isOpen && (_jsx("div", { style: {
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '300px',
                            overflowY: 'auto',
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(0, 37, 74, 0.3)',
                            borderRadius: '14px',
                            marginTop: '4px',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        }, children: field.options?.map((option) => {
                            const isChecked = selectedValues.includes(option.value);
                            return (_jsxs("button", { type: "button", onClick: () => handleToggle(option.value), style: {
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontSize: '16px',
                                    color: '#21262a',
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#f4f4f4';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }, children: [_jsx("img", { src: isChecked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true", style: { width: '24px', height: '24px', flexShrink: 0 } }), _jsx("span", { style: { flex: 1 }, children: _jsx(MarkdownText, { children: getLocalizedValue(option.title, locale) }) })] }, option.value));
                        }) }))] }), field.helperText && (_jsx("p", { className: classNames?.helperText, children: getLocalizedValue(field.helperText, locale) }))] }));
};
/**
 * Renders a multi-select dropdown with checkboxes
 */
const renderMultiSelectDropdown = (field, selectedValues, onChange, locale, classNames, isDisabled) => (_jsx(MultiSelectDropdown, { field: field, selectedValues: selectedValues, onChange: onChange, locale: locale, classNames: classNames, isDisabled: isDisabled }));
/**
 * Renders a checkbox group field (multi-select checkboxes)
 */
const renderCheckboxGroup = (field, selectedValues, onToggle, locale, classNames) => (_jsxs("div", { className: classNames?.radioGroup, children: [_jsx("div", { className: classNames?.radioGroupHeader, children: _jsx(FieldLabel, { field: field, locale: locale, className: classNames?.fieldLabel, as: "div" }) }), _jsx("div", { className: classNames?.radioOptions, children: field.options?.map((option) => {
                const isChecked = selectedValues.includes(option.value);
                return (_jsxs("button", { type: "button", className: `${classNames?.radioOption || ''} ${isChecked ? classNames?.radioOptionChecked || 'checked' : ''}`.trim(), onClick: () => onToggle(option.value), children: [_jsx("div", { className: classNames?.radioIcon, children: _jsx("img", { src: isChecked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true" }) }), _jsx("span", { className: classNames?.radioLabel, children: _jsx(MarkdownText, { children: getLocalizedValue(option.title, locale) }) })] }, option.value));
            }) })] }));
/**
 * Renders terms and conditions checkbox with links
 */
const renderTermsCheckbox = (field, locale, checked, onChange, classNames, errorMessage) => {
    const labelText = getLocalizedValue(field.title, locale);
    const hasError = !!errorMessage;
    const handleContainerClick = (e) => {
        const target = e.target;
        if (target.tagName !== 'A' && !target.closest('a')) {
            onChange(!checked);
        }
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: classNames?.termsWrapper, onClick: handleContainerClick, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onChange(!checked);
                    }
                }, role: "checkbox", tabIndex: 0, "aria-checked": checked, "aria-invalid": hasError, "aria-describedby": hasError ? `${field.fieldName}-error` : undefined, children: [_jsx("button", { type: "button", className: classNames?.checkbox, onClick: (e) => {
                            e.stopPropagation();
                            onChange(!checked);
                        }, "aria-label": "Terms and conditions checkbox", children: _jsx("img", { src: checked ? CheckboxChecked : CheckboxUnchecked, alt: "", "aria-hidden": "true" }) }), _jsx("span", { className: classNames?.termsText, children: _jsx(MarkdownText, { children: labelText }) })] }), errorMessage && (_jsx("p", { id: `${field.fieldName}-error`, className: classNames?.helperText, style: { color: '#d32f2f', marginTop: '4px', fontSize: '14px' }, children: errorMessage }))] }));
};
/**
 * Renders a visual divider
 */
const renderDivider = (_field, classNames) => (_jsx("div", { className: classNames?.divider }));
/**
 * TurnstileWidget Component - Cloudflare Turnstile CAPTCHA
 * Extracted as a proper component to maintain stable refs across renders
 */
const TurnstileWidget = ({ field, onChange, locale, classNames, errorMessage }) => {
    const turnstileRef = React.useRef(null);
    const widgetIdRef = React.useRef(null);
    const hasInitializedRef = React.useRef(false);
    const siteKey = getTurnstileSiteKey();
    useEffect(() => {
        // Prevent double initialization
        if (hasInitializedRef.current)
            return;
        const initializeTurnstile = () => {
            if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
                try {
                    const widgetId = window.turnstile.render(turnstileRef.current, {
                        sitekey: siteKey,
                        theme: 'light',
                        callback: (token) => {
                            onChange(token);
                        },
                        'error-callback': () => {
                            onChange('');
                        },
                        'expired-callback': () => {
                            onChange('');
                            if (widgetIdRef.current && window.turnstile) {
                                window.turnstile.reset(widgetIdRef.current);
                            }
                        },
                    });
                    widgetIdRef.current = widgetId;
                    hasInitializedRef.current = true;
                }
                catch (error) {
                    console.error('[Turnstile] Failed to render widget:', error);
                }
            }
        };
        // Load Turnstile script if not already loaded
        if (!window.turnstile) {
            const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
            if (existingScript) {
                // Script is loading, wait for it
                existingScript.addEventListener('load', initializeTurnstile);
                return () => {
                    existingScript.removeEventListener('load', initializeTurnstile);
                };
            }
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.onload = initializeTurnstile;
            document.head.appendChild(script);
        }
        else {
            // Script already loaded
            initializeTurnstile();
        }
        return () => {
            // Cleanup: remove widget on unmount
            if (widgetIdRef.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                }
                catch (error) {
                    console.warn('[Turnstile] Error removing widget:', error);
                }
                widgetIdRef.current = null;
            }
            hasInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs("div", { className: classNames?.field, children: [_jsx("div", { className: classNames?.inputWrapper, children: _jsx("div", { ref: turnstileRef }) }), errorMessage && (_jsx("p", { id: `${field.fieldName}-error`, className: classNames?.helperText, style: { color: '#d32f2f', marginTop: '4px', fontSize: '14px' }, children: errorMessage })), !errorMessage && field.helperText && (_jsx("p", { className: classNames?.helperText, children: getLocalizedValue(field.helperText, locale) }))] }));
};
/**
 * JsonForm - A generic form component driven by JSON schema
 *
 * Renders form fields based on a JSON schema definition.
 * Supports: text, email, number, dropdown, dropdown-multi, checkbox, hidden, termsCheckbox, divider, reCAPTCHA (Cloudflare Turnstile)
 *
 * @example
 * ```tsx
 * const schema = {
 *   fields: [
 *     { fieldName: 'schoolId', type: 'hidden' },
 *     { title: { 'en-US': 'Name' }, fieldName: 'name', type: 'text' },
 *     { title: { 'en-US': 'Email' }, fieldName: 'email', type: 'email' },
 *     { title: { 'en-US': 'Countries' }, fieldName: 'countries', type: 'dropdown-multi', options: [...] },
 *     { fieldName: 'divider1', type: 'divider' },
 *     { fieldName: 'turnstile', type: 'reCAPTCHA', validation: { required: true } },
 *     { fieldName: 'acceptedTerms', type: 'termsCheckbox', validation: { required: true } }
 *   ]
 * };
 *
 * const [values, setValues] = useState(() => initializeFormValues(schema));
 *
 * <JsonForm
 *   schema={schema}
 *   values={values}
 *   onChange={(field, value) => setValues(prev => ({ ...prev, [field]: value }))}
 * />
 * ```
 */
const JsonForm = ({ schema, values, onChange, onValidityChange, locale = 'en-US', classNames = {}, readOnlyFields = [], disabledFields = [], children, }) => {
    // Calculate validity and field errors whenever values change
    const [fieldErrors, setFieldErrors] = React.useState({});
    useEffect(() => {
        const isValid = validateFormValues(values, schema);
        onValidityChange?.(isValid);
        // Calculate field errors
        const errors = {};
        const calculateErrors = (fields) => {
            for (const field of fields) {
                if (field.type === 'divider' || field.type === 'hidden')
                    continue;
                // reCAPTCHA always uses canonical 'turnstile' key (see onChange above)
                const fieldValue = field.type === 'reCAPTCHA' ? values['turnstile'] : values[field.fieldName];
                const error = getFieldError(field, fieldValue);
                errors[field.fieldName] = error;
                // Check conditional children
                if (field.type === 'dropdown' && field.options) {
                    const selectedValue = values[field.fieldName];
                    if (selectedValue) {
                        const selectedOption = field.options.find(opt => opt.value === selectedValue);
                        if (selectedOption?.children) {
                            calculateErrors(selectedOption.children);
                        }
                    }
                }
            }
        };
        calculateErrors(schema.fields);
        setFieldErrors(errors);
    }, [values, schema, onValidityChange]);
    /**
     * Handles checkbox toggle for multi-select checkbox groups
     */
    const handleCheckboxToggle = (fieldName, optionValue) => {
        const currentValues = values[fieldName] || [];
        const newValues = currentValues.includes(optionValue)
            ? currentValues.filter((v) => v !== optionValue)
            : [...currentValues, optionValue];
        onChange(fieldName, newValues);
    };
    /**
     * Renders a single field based on its type
     */
    const renderField = (field) => {
        const isReadOnly = readOnlyFields.includes(field.fieldName) || field.readOnly;
        const isDisabled = disabledFields.includes(field.fieldName) || field.disabled;
        switch (field.type) {
            case 'text':
            case 'email':
            case 'number':
                return renderTextInput(field, values[field.fieldName] || '', (value) => onChange(field.fieldName, value), locale, classNames, isReadOnly || false, isDisabled || false, fieldErrors[field.fieldName]);
            case 'dropdown': {
                const dropdownValue = values[field.fieldName] || '';
                const children = getDropdownChildren(field, dropdownValue);
                return (_jsxs(React.Fragment, { children: [renderDropdown(field, dropdownValue, (value) => onChange(field.fieldName, value), locale, classNames, isDisabled || false), children.map(childField => (_jsx(React.Fragment, { children: renderField(childField) }, childField.fieldName)))] }));
            }
            case 'dropdown-multi':
                return renderMultiSelectDropdown(field, values[field.fieldName] || [], (selectedValues) => onChange(field.fieldName, selectedValues), locale, classNames, isDisabled || false);
            case 'checkbox':
                return renderCheckboxGroup(field, values[field.fieldName] || [], (optionValue) => handleCheckboxToggle(field.fieldName, optionValue), locale, classNames);
            case 'termsCheckbox':
                return renderTermsCheckbox(field, locale, values[field.fieldName] === true, (checked) => onChange(field.fieldName, checked), classNames, fieldErrors[field.fieldName]);
            case 'divider':
                return renderDivider(field, classNames);
            case 'reCAPTCHA':
                // Always use the canonical 'turnstile' key regardless of the schema's fieldName.
                // CDN schemas may omit fieldName or use arbitrary names; this ensures the token
                // always lands at formData.turnstile where the transform functions expect it.
                return (_jsx(TurnstileWidget, { field: field, onChange: (value) => onChange('turnstile', value), locale: locale, classNames: classNames, errorMessage: null }));
            case 'hidden':
                // Hidden fields don't render anything
                return null;
            default:
                return null;
        }
    };
    return (_jsxs("form", { className: classNames.form, children: [schema.fields.map((field) => {
                const rendered = renderField(field);
                // If renderField returns null (hidden fields), don't render anything
                if (rendered === null)
                    return null;
                // Always wrap in Fragment with key to ensure React can track the element
                return (_jsx(React.Fragment, { children: rendered }, field.fieldName));
            }), children] }));
};
export default JsonForm;
